import React, { useState, useEffect } from 'react';
import { Star, Clock, Trash2, Car, Wrench, TrendingDown, Calendar, CheckCircle, AlertCircle, Users, ChevronDown, ChevronUp } from 'lucide-react';

const ResourceOptimization: React.FC = () => {
  const [selectedCleaningTasks, setSelectedCleaningTasks] = useState<string[]>([]);
  const [bookedDesks, setBookedDesks] = useState<{ [key: string]: boolean }>({});
  const [cleaningInProgress, setCleaningInProgress] = useState<string[]>([]);
  const [expandedTasks, setExpandedTasks] = useState<string[]>([]);
  const [utilizationData, setUtilizationData] = useState<{ [key: string]: number }>({});

  // Mock data
  const resourceData = {
    cleaningSchedule: {
      todayTasks: [
        {
          id: 'CT-001',
          area: 'Conference Room A',
          priority: 'high',
          lastCleaned: '2 hours ago',
          traffic: 'Heavy',
          status: 'Pending',
          utilization: 85,
          utilizationThreshold: 75,
          cleaningCrew: {
            name: 'Team Alpha',
            dispatchTime: '5:30 PM',
            estimatedArrival: '6:00 PM',
            status: 'En Route',
            contactNumber: '555-0123'
          }
        },
        {
          id: 'CT-002',
          area: 'Open Office North',
          priority: 'medium',
          lastCleaned: '4 hours ago',
          traffic: 'Moderate',
          status: 'Pending',
          utilization: 72,
          utilizationThreshold: 75,
          cleaningCrew: null
        },
        {
          id: 'CT-003',
          area: 'Break Room',
          priority: 'high',
          lastCleaned: '1 hour ago',
          traffic: 'Heavy',
          status: 'In Progress',
          utilization: 92,
          utilizationThreshold: 80,
          cleaningCrew: {
            name: 'Team Beta',
            dispatchTime: '5:15 PM',
            estimatedArrival: '5:45 PM',
            status: 'Cleaning in Progress',
            contactNumber: '555-0124'
          }
        },
        {
          id: 'CT-004',
          area: 'Executive Wing',
          priority: 'medium',
          lastCleaned: '3 hours ago',
          traffic: 'Moderate',
          status: 'Scheduled',
          utilization: 88,
          utilizationThreshold: 70,
          cleaningCrew: {
            name: 'Team Gamma',
            dispatchTime: '5:45 PM',
            estimatedArrival: '6:15 PM',
            status: 'Scheduled',
            contactNumber: '555-0125'
          }
        }
      ],
      completedToday: 12,
      nextScheduled: 5
    },
    deskUtilization: {
      available: [
        { id: 'D-101', type: 'Standing', location: 'North Wing', status: 'Available' },
        { id: 'D-102', type: 'Regular', location: 'North Wing', status: 'Available' },
        { id: 'D-103', type: 'Standing', location: 'South Wing', status: 'Available' },
        { id: 'D-104', type: 'Regular', location: 'East Wing', status: 'Available' }
      ],
      assigned: 85,
      hotDesks: 25,
      utilization: 78
    },
    supplies: {
      inventory: [
        { item: 'Paper Towels', current: 75, threshold: 20, daysRemaining: 15 },
        { item: 'Hand Soap', current: 45, threshold: 30, daysRemaining: 8 },
        { item: 'Printer Paper', current: 25, threshold: 20, daysRemaining: 5 },
        { item: 'Coffee Supplies', current: 15, threshold: 25, daysRemaining: 3 }
      ],
      recentOrders: [
        { date: '2024-03-15', items: ['Printer Paper', 'Coffee Supplies'], status: 'Delivered' },
        { date: '2024-03-10', items: ['Hand Soap', 'Paper Towels'], status: 'Delivered' }
      ]
    },
    parking: {
      total: 150,
      occupied: 112,
      reserved: 95,
      visitor: 15,
      peakHours: '9:00 AM - 11:00 AM',
      recommendations: [
        { type: 'Visitor Spaces', action: 'Increase by 5', reason: 'High demand during events' },
        { type: 'EV Charging', action: 'Add 3 stations', reason: 'Waiting list growing' }
      ]
    }
  };

  // Simulated real-time utilization updates
  useEffect(() => {
    const interval = setInterval(() => {
      setUtilizationData(prev => {
        const newData = { ...prev };
        resourceData.cleaningSchedule.todayTasks.forEach(task => {
          // Randomly adjust utilization by ±5%
          const change = Math.random() * 10 - 5;
          newData[task.id] = Math.min(100, Math.max(0, (prev[task.id] || task.utilization) + change));
        });
        return newData;
      });
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleCleaningTaskSelect = (taskId: string) => {
    setSelectedCleaningTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const toggleTaskExpansion = (taskId: string) => {
    setExpandedTasks(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const getUtilizationStatus = (utilization: number, threshold: number) => {
    if (utilization >= threshold) return 'requires-cleaning';
    if (utilization >= threshold * 0.8) return 'warning';
    return 'normal';
  };

  const getUtilizationColor = (utilization: number, threshold: number) => {
    const status = getUtilizationStatus(utilization, threshold);
    switch (status) {
      case 'requires-cleaning':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-500';
      default:
        return 'text-green-500';
    }
  };

  const getCleaningStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'cleaning in progress':
        return 'text-blue-500';
      case 'en route':
        return 'text-yellow-500';
      case 'scheduled':
        return 'text-purple-500';
      default:
        return 'text-gray-500';
    }
  };

  const handleDeskBooking = (deskId: string) => {
    setBookedDesks(prev => ({
      ...prev,
      [deskId]: !prev[deskId]
    }));
    // In a real app, this would update the booking system
    console.log(`${bookedDesks[deskId] ? 'Canceling' : 'Booking'} desk:`, deskId);
  };

  const getSupplyStatusColor = (current: number, threshold: number) => {
    if (current <= threshold) return 'text-red-500';
    if (current <= threshold * 1.5) return 'text-yellow-500';
    return 'text-green-500';
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Resource Optimization</h2>
      
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Clock className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Cleaning Tasks</h3>
          </div>
          <p className="text-3xl font-bold">{resourceData.cleaningSchedule.completedToday}</p>
          <p className="text-gray-500">Completed today</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Star className="text-yellow-500 mr-2" />
            <h3 className="text-lg font-semibold">Desk Usage</h3>
          </div>
          <p className="text-3xl font-bold">{resourceData.deskUtilization.utilization}%</p>
          <p className="text-gray-500">Current utilization</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Trash2 className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Low Supplies</h3>
          </div>
          <p className="text-3xl font-bold">
            {resourceData.supplies.inventory.filter(item => item.current <= item.threshold).length}
          </p>
          <p className="text-gray-500">Items to reorder</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Car className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Parking</h3>
          </div>
          <p className="text-3xl font-bold">{resourceData.parking.occupied}</p>
          <p className="text-gray-500">Spaces in use</p>
        </div>
      </div>

      {/* Enhanced Cleaning Schedule */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">Area Cleaning Status</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Auto-dispatch threshold:</span>
              <span className="text-sm font-medium">75% utilization</span>
            </div>
          </div>
          <div className="space-y-4">
            {resourceData.cleaningSchedule.todayTasks.map((task) => (
              <div key={task.id} className="border rounded-lg p-4">
                <div 
                  className="cursor-pointer"
                  onClick={() => toggleTaskExpansion(task.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-grow">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">{task.area}</h4>
                        {task.cleaningCrew && (
                          <span className={`text-sm font-medium ${getCleaningStatusColor(task.cleaningCrew.status)}`}>
                            {task.cleaningCrew.status}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Current Utilization:</span>
                          <span className={`text-sm font-medium ${getUtilizationColor(
                            utilizationData[task.id] || task.utilization,
                            task.utilizationThreshold
                          )}`}>
                            {Math.round(utilizationData[task.id] || task.utilization)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`rounded-full h-2 ${
                              getUtilizationStatus(utilizationData[task.id] || task.utilization, task.utilizationThreshold) === 'requires-cleaning'
                                ? 'bg-red-500'
                                : getUtilizationStatus(utilizationData[task.id] || task.utilization, task.utilizationThreshold) === 'warning'
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                            }`}
                            style={{ width: `${utilizationData[task.id] || task.utilization}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4">
                      {expandedTasks.includes(task.id) ? (
                        <ChevronUp className="text-gray-400" />
                      ) : (
                        <ChevronDown className="text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Cleaning Crew Information */}
                {expandedTasks.includes(task.id) && task.cleaningCrew && (
                  <div className="mt-4 pt-4 border-t">
                    <h5 className="font-medium mb-2">Cleaning Crew Details</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Team:</span>
                        <span className="text-sm font-medium">{task.cleaningCrew.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Dispatched:</span>
                        <span className="text-sm font-medium">{task.cleaningCrew.dispatchTime}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">ETA:</span>
                        <span className="text-sm font-medium">{task.cleaningCrew.estimatedArrival}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Contact:</span>
                        <span className="text-sm font-medium">{task.cleaningCrew.contactNumber}</span>
                      </div>
                      <div className="flex justify-between items-center mt-3">
                        <span className={`text-sm font-medium ${getCleaningStatusColor(task.cleaningCrew.status)}`}>
                          {task.cleaningCrew.status}
                        </span>
                        <button className="px-3 py-1 text-sm rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200">
                          Contact Team
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Desk Booking */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Available Desks</h3>
          <div className="space-y-4">
            {resourceData.deskUtilization.available.map((desk) => (
              <div key={desk.id} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium">{desk.type} Desk</h4>
                    <p className="text-sm text-gray-500">{desk.location}</p>
                  </div>
                  <button
                    onClick={() => handleDeskBooking(desk.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      bookedDesks[desk.id]
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {bookedDesks[desk.id] ? 'Cancel Booking' : 'Book Now'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Supplies and Parking */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Supply Inventory</h3>
          <div className="space-y-4">
            {resourceData.supplies.inventory.map((item, index) => (
              <div key={index} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{item.item}</h4>
                    <p className="text-sm text-gray-500">
                      {item.daysRemaining} days remaining
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`font-medium ${getSupplyStatusColor(item.current, item.threshold)}`}>
                      {item.current}% remaining
                    </span>
                    {item.current <= item.threshold && (
                      <button className="block mt-1 text-sm text-blue-500 hover:text-blue-600">
                        Reorder Now
                      </button>
                    )}
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`rounded-full h-2 ${
                      item.current <= item.threshold ? 'bg-red-500' :
                      item.current <= item.threshold * 1.5 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${item.current}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Parking Optimization</h3>
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-600">Current Occupancy</span>
              <span className="font-medium">
                {resourceData.parking.occupied} / {resourceData.parking.total}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 rounded-full h-2"
                style={{ width: `${(resourceData.parking.occupied / resourceData.parking.total) * 100}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Peak hours: {resourceData.parking.peakHours}
            </p>
          </div>
          <div className="space-y-4">
            {resourceData.parking.recommendations.map((rec, index) => (
              <div key={index} className="flex items-start space-x-3 border-b pb-4 last:border-b-0">
                <div className="flex-grow">
                  <h4 className="font-medium">{rec.type}</h4>
                  <p className="text-sm text-gray-600">{rec.reason}</p>
                </div>
                <button className="px-3 py-1 text-sm rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200">
                  {rec.action}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceOptimization; 